'use client'

import { Calendar } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'
import { cn } from '@/lib/utils'

export function FilterBar() {
    const router = useRouter()
    const searchParams = useSearchParams()

    // Get current values
    const currentSort = searchParams.get('sort') || 'latest'
    const currentCategory = searchParams.get('category') || 'All Categories'
    const currentTimeRange = searchParams.get('timeRange') || 'All Time'
    const currentDate = searchParams.get('date') || ''

    // Helper to update URL
    const updateFilter = useCallback((key: string, value: string) => {
        const params = new URLSearchParams(searchParams.toString())

        if (value && value !== 'All Categories' && value !== 'All Time') {
            params.set(key, value)
        } else {
            params.delete(key)
        }

        // Reset page when filtering
        params.delete('page')

        router.push(`/?${params.toString()}`)
    }, [router, searchParams])

    return (
        <div className="bg-soft-wash border-b border-card-border sticky top-16 z-30 hidden md:block">
            <div className="max-w-[1400px] mx-auto px-6 py-3">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-semibold text-ink-black font-sans">Filter:</span>

                    {/* Date Picker */}
                    <div className={cn(
                        "flex items-center gap-2 px-4 py-2 bg-white border rounded-lg transition-colors group",
                        currentDate ? "border-trail-blue ring-1 ring-trail-blue" : "border-gray-300 hover:border-trail-blue"
                    )}>
                        <Calendar className={cn("w-4 h-4", currentDate ? "text-trail-blue" : "text-gray-500")} />
                        <label htmlFor="date-filter" className="sr-only">Select Date</label>
                        <input
                            id="date-filter"
                            name="date"
                            type="date"
                            className="text-sm border-none focus:outline-none cursor-pointer text-gray-700 font-mono bg-transparent"
                            value={currentDate}
                            onChange={(e) => updateFilter('date', e.target.value)}
                        />
                    </div>

                    {/* Sort Options */}
                    {[
                        { label: 'Latest', value: 'latest' },
                        { label: 'Most Popular', value: 'popular' },
                        { label: 'Trending', value: 'trending' },
                        { label: '🔥 Hot', value: 'hot' },
                    ].map((option) => (
                        <button
                            key={option.value}
                            onClick={() => updateFilter('sort', option.value)}
                            className={cn(
                                "px-4 py-2 text-sm font-medium rounded-lg font-sans transition-colors",
                                currentSort === option.value
                                    ? "bg-trail-blue text-white shadow-sm hover:bg-blue-700"
                                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                            )}
                        >
                            {option.label}
                        </button>
                    ))}

                    {/* Category Filter */}
                    <label htmlFor="category-filter" className="sr-only">Filter by Category</label>
                    <select
                        id="category-filter"
                        name="category"
                        value={currentCategory}
                        onChange={(e) => updateFilter('category', e.target.value)}
                        className={cn(
                            "px-4 py-2 bg-white border text-sm rounded-lg cursor-pointer text-gray-700 font-sans focus:outline-none",
                            currentCategory !== 'All Categories' ? "border-trail-blue ring-1 ring-trail-blue" : "border-gray-300 hover:border-trail-blue"
                        )}
                    >
                        <option>All Categories</option>
                        <option>Politics</option>
                        <option>Tech</option>
                        <option>Culture</option>
                        <option>Business</option>
                        <option>World</option>
                    </select>

                    {/* Time Range */}
                    <label htmlFor="time-range-filter" className="sr-only">Filter by Time Range</label>
                    <select
                        id="time-range-filter"
                        name="timeRange"
                        value={currentTimeRange}
                        onChange={(e) => updateFilter('timeRange', e.target.value)}
                        className={cn(
                            "px-4 py-2 bg-white border text-sm rounded-lg cursor-pointer text-gray-700 font-sans focus:outline-none",
                            currentTimeRange !== 'All Time' ? "border-trail-blue ring-1 ring-trail-blue" : "border-gray-300 hover:border-trail-blue"
                        )}
                    >
                        <option>All Time</option>
                        <option>Last 24 Hours</option>
                        <option>This Week</option>
                        <option>This Month</option>
                    </select>
                </div>
            </div>
        </div>
    )
}
